import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { 
  Trophy, Users, TrendingUp, Target, Activity, 
  BarChart3, Calendar, Star, Crown, Zap, Skull,
  ChevronDown, Menu, X, Award, Filter, Search,
  Dumbbell, BookOpen, Medal, Camera, Lock, History
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, ScatterChart, Scatter, PieChart, Pie, Cell } from 'recharts';

// Import components
import HeadToHead from './components/HeadToHead';
import AllTimeStandingsComponent from './components/AllTimeStandings';
import RecordBookComponent from './components/RecordBook';
import DraftExplorer from './components/DraftExplorer';
import PowerRankings from './components/PowerRankings';
import ManagerCards from './components/ManagerCards';
import LegacyScore from './components/LegacyScore';
import PlayerRingOfFame from './components/PlayerRingOfFame';
import LeaguePerformance from './components/LeaguePerformance';
import HallOfChampions from './components/HallOfChampions';
import HallOfSackos from './components/HallOfSackos';
import CelebrityAppearances from './components/CelebrityAppearances';
import About from './components/About';
import Keepers from './components/Keepers';
import DraftWarRoom from './components/DraftWarRoom'; // unlisted page — no sidebar tab on purpose
import PlayerHistory from './components/PlayerHistory';

// Main App Component with Routing
const AppContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  // Data states
  const [h2hData, setH2hData] = useState([]);
  const [finalData, setFinalData] = useState([]);
  const [draftData, setDraftData] = useState([]);
  const [legacyData, setLegacyData] = useState([]);
  const [currentYearPR, setCurrentYearPR] = useState([]);
  const [allTimeRank, setAllTimeRank] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const managers = ['Kevin', 'Jake', 'Lyons', 'Nipples', 'Dan', 'Shuds', 'Karp', 'Mills', 'Joey', 'Raffy', 'Oster', 'Scott', 'Sam'];

  // Route mappings
  const routeToTabMap = {
    '/': 'head-to-head',
    '/headtohead': 'head-to-head',
    '/standings': 'standings',
    '/powerrankings': 'power-rankings',
    '/draftexplorer': 'draft-explorer',
    '/keepers': 'keepers',
    '/playerhistory': 'player-history',
    '/managercards': 'manager-cards',
    '/legacy': 'legacy',
    '/recordbook': 'record-book',
    '/ringoffame': 'ring-of-fame',
    '/leagueperformance': 'league-performance',
    '/celebrityappearances': 'celebrity-appearances',
    '/hallofchampions': 'hall-of-champions',
    '/hallofsackos': 'hall-of-sackos',
    '/about': 'about'
  };

  const tabToRouteMap = {
    'head-to-head': '/',
    'standings': '/standings',
    'power-rankings': '/powerrankings',
    'draft-explorer': '/draftexplorer',
    'keepers': '/keepers',
    'player-history': '/playerhistory',
    'manager-cards': '/managercards',
    'legacy': '/legacy',
    'record-book': '/recordbook',
    'ring-of-fame': '/ringoffame',
    'league-performance': '/leagueperformance',
    'celebrity-appearances': '/celebrityappearances',
    'hall-of-champions': '/hallofchampions',
    'hall-of-sackos': '/hallofsackos',
    'about': '/about'
  };

  // Get current active tab from URL
  const getCurrentTab = () => {
    return routeToTabMap[location.pathname] || 'head-to-head';
  };

  const activeTab = getCurrentTab();

  // Function to handle tab changes and navigate
  const handleTabChange = (tabId) => {
    const route = tabToRouteMap[tabId];
    if (route) {
      navigate(route);
    }
    
    // Scroll main content area to top
    const mainContent = document.querySelector('main[class*="overflow-y-auto"]');
    if (mainContent) {
      mainContent.scrollTo({ top: 0, behavior: 'smooth' });
    }
    
    // Also scroll window to top as fallback for mobile
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const tabs = [
    { id: 'head-to-head', label: 'Head to Head', icon: Users },
    { id: 'standings', label: 'All-Time Standings', icon: Trophy },
    { id: 'power-rankings', label: 'Power Rankings', icon: Dumbbell },
    { id: 'draft-explorer', label: 'Draft Explorer', icon: Target },
    { id: 'keepers', label: 'Keepers', icon: Lock },
    { id: 'player-history', label: 'Player History', icon: History },
    { id: 'manager-cards', label: 'Manager Cards', icon: Star },
    { id: 'legacy', label: 'Legacy Score', icon: BookOpen },
    { id: 'record-book', label: 'Record Book', icon: Award },
    { id: 'ring-of-fame', label: 'Player Ring of Fame', icon: Medal },
    { id: 'league-performance', label: 'League Performance', icon: Activity },
    { id: 'celebrity-appearances', label: 'Celeb Appearances', icon: Camera },
    { id: 'hall-of-champions', label: 'Hall of Champions', icon: Crown },
    { id: 'hall-of-sackos', label: 'Hall of Sackos', icon: Skull }
  ];

  // Dynamic theming based on active tab
  const getThemeClasses = () => {
    switch (activeTab) {
      case 'hall-of-sackos':
        return {
          sidebarGradient: 'from-gray-900 via-red-900 to-black',
          borderColor: 'border-red-500/20',
          buttonActive: 'bg-red-500/20',
          textPrimary: 'text-white',
          textSecondary: 'text-red-200',
          textAccent: 'text-red-400',
          iconColor: 'text-red-400',
          mobileGradient: 'from-gray-900 via-red-900 to-black',
          mobileBorder: 'border-red-500/20',
          // Cartoonish hover colors
          hoverTextColor: 'text-red-300',
          hoverIconColor: 'text-red-100'
        };
      case 'hall-of-champions':
        return {
          sidebarGradient: 'from-purple-900 via-yellow-600 to-amber-800',
          borderColor: 'border-yellow-500/20',
          buttonActive: 'bg-yellow-500/20',
          textPrimary: 'text-white',
          textSecondary: 'text-yellow-200',
          textAccent: 'text-yellow-400',
          iconColor: 'text-yellow-400',
          mobileGradient: 'from-purple-900 via-yellow-600 to-amber-800',
          mobileBorder: 'border-yellow-500/20',
          // Cartoonish hover colors
          hoverTextColor: 'text-yellow-100',
          hoverIconColor: 'text-white'
        };
      case 'celebrity-appearances':
        return {
          sidebarGradient: 'from-orange-900 via-red-800 to-purple-900',
          borderColor: 'border-orange-500/20',
          buttonActive: 'bg-orange-500/20',
          textPrimary: 'text-white',
          textSecondary: 'text-orange-200',
          textAccent: 'text-orange-400',
          iconColor: 'text-orange-400',
          mobileGradient: 'from-orange-900 via-red-800 to-purple-900',
          mobileBorder: 'border-orange-500/20',
          // Cartoonish hover colors
          hoverTextColor: 'text-orange-300',
          hoverIconColor: 'text-orange-100'
        };
      default:
        return {
          sidebarGradient: 'from-purple-900 via-purple-800 to-indigo-900',
          borderColor: 'border-purple-500/20',
          buttonActive: 'bg-white/20',
          textPrimary: 'text-white',
          textSecondary: 'text-purple-200',
          textAccent: 'text-yellow-400',
          iconColor: 'text-yellow-400',
          mobileGradient: 'from-purple-900 via-purple-800 to-indigo-900',
          mobileBorder: 'border-purple-500/20',
          // Cartoonish hover colors
          hoverTextColor: 'text-cyan-300',
          hoverIconColor: 'text-white'
        };
    }
  };

  const theme = getThemeClasses();

  // Load Excel data on component mount
  useEffect(() => {
    const loadExcelData = async () => {
      try {
        setLoading(true);
        
        // Check if we have the uploaded file available
        if (typeof window !== 'undefined' && window.fs && window.fs.readFile) {
          // Read the uploaded Excel file
          const arrayBuffer = await window.fs.readFile('BoBData.xlsx');
          
          // Load XLSX library if not already loaded
          if (!window.XLSX) {
            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
            script.onload = () => {
              parseExcelData(arrayBuffer);
            };
            script.onerror = () => {
              setError('Failed to load XLSX library');
              setLoading(false);
            };
            document.head.appendChild(script);
          } else {
            parseExcelData(arrayBuffer);
          }
        } else {
          // Fallback: try to fetch from public folder
          const response = await fetch('/BoBData.xlsx');
          if (!response.ok) {
            throw new Error('Could not fetch Excel file');
          }
          const arrayBuffer = await response.arrayBuffer();
          
          // Load XLSX library if not already loaded
          if (!window.XLSX) {
            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
            script.onload = () => {
              parseExcelData(arrayBuffer);
            };
            script.onerror = () => {
              setError('Failed to load XLSX library');
              setLoading(false);
            };
            document.head.appendChild(script);
          } else {
            parseExcelData(arrayBuffer);
          }
        }
        
      } catch (err) {
        console.error('Error loading Excel data:', err);
        setError('Error loading Excel data: ' + err.message);
        setLoading(false);
      }
    };

    const parseExcelData = (arrayBuffer) => {
      try {
        // Parse Excel file
        const workbook = window.XLSX.read(arrayBuffer, {
          cellStyles: true,
          cellFormulas: true,
          cellDates: true,
          cellNF: true,
          sheetStubs: true
        });

        console.log('Available sheets:', workbook.SheetNames);

        // Process H2H data
        if (workbook.SheetNames.includes('H2H')) {
          const h2hWorksheet = workbook.Sheets['H2H'];
          const h2hJsonData = window.XLSX.utils.sheet_to_json(h2hWorksheet);
          
          // Clean and validate H2H data
          const cleanedH2hData = h2hJsonData.map(row => ({
            ...row,
            Year: parseInt(row.Year) || 0,
            PF: parseFloat(row.PF) || 0,
            PA: parseFloat(row.PA) || 0,
            MoV: parseFloat(row.MoV) || 0,
            IsWin: parseInt(row.IsWin) || 0,
            IsLoss: parseInt(row.IsLoss) || 0
          }));
          
          setH2hData(cleanedH2hData);
          console.log('H2H Data loaded:', cleanedH2hData.length, 'records');
        } else {
          console.error('H2H sheet not found');
          setError('H2H sheet not found in Excel file');
        }

        // Process Final Data
        if (workbook.SheetNames.includes('Final Data')) {
          const finalDataWorksheet = workbook.Sheets['Final Data'];
          const finalDataJsonData = window.XLSX.utils.sheet_to_json(finalDataWorksheet);
          
          // Clean and validate Final Data
          const cleanedFinalData = finalDataJsonData.map(row => ({
            ...row,
            Year: parseInt(row.Year) || 0,
            Wins: parseInt(row.Wins) || 0,
            Losses: parseInt(row.Losses) || 0,
            WinPct: parseFloat(row.WinPct) || 0,
            PF: parseFloat(row.PF) || 0,
            PA: parseFloat(row.PA) || 0,
            Standings: parseInt(row.Standings) || 0,
            Payout: parseFloat(row.Payout) || 0
          }));
          
          setFinalData(cleanedFinalData);
          console.log('Final Data loaded:', cleanedFinalData.length, 'records');
        } else {
          console.error('Final Data sheet not found');
          setError('Final Data sheet not found in Excel file');
        }

        // Find draft data sheet (usually around sheet 25 or look for draft-related names)
        let draftSheet = null;
        let draftSheetName = '';
        
        // Try to find draft data sheet by index (sheet 25, which is index 24)
        if (workbook.SheetNames.length >= 25) {
          draftSheetName = workbook.SheetNames[24]; // 0-indexed, so sheet 25 is index 24
          draftSheet = workbook.Sheets[draftSheetName];
          console.log('Found sheet at index 24:', draftSheetName);
        }
        
        // If that doesn't work, look for sheets with draft-related names
        if (!draftSheet) {
          const draftSheetNames = workbook.SheetNames.filter(name => 
            name.toLowerCase().includes('draft') || 
            name.toLowerCase().includes('dd') ||
            name.toLowerCase().includes('player') ||
            name.toLowerCase().includes('keeper')
          );
          
          if (draftSheetNames.length > 0) {
            draftSheetName = draftSheetNames[0];
            draftSheet = workbook.Sheets[draftSheetName];
            console.log('Found draft sheet by name:', draftSheetName);
          }
        }
        
        // Parse draft data if found
        if (draftSheet) {
          const draftJsonData = window.XLSX.utils.sheet_to_json(draftSheet);
          
          const processedDraftData = draftJsonData.map((row) => {
            const year = row.Year || row.year || row.YEAR;
            const manager = row.Owner || row.owner || row.OWNER || row.Manager || row.manager;
            const player = row.Player || row.player || row.PLAYER || row.Name || row.name;
            const position = row.Position || row.position || row.POSITION || row.Pos || row.pos;
            const cost = parseFloat(row.Cost || row.cost || row.COST || row.Price || row.price || 0);
            const investment = row['Keeper.Status'] || row['Keeper Status'] || row.KeeperStatus || 
            row.keeper_status || row.Investment || row.Type || 'Drafted';

            // Validate required fields
            if (!year || !manager || !player || !position || isNaN(cost)) {
              return null;
            }
            
            return {
              Year: parseInt(year),
              Manager: String(manager).trim(),
              Player: String(player).trim(),
              Position: String(position).trim().toUpperCase(),
              Cost: cost,
              Investment: String(investment).trim()
            };
          }).filter(Boolean);
          
          setDraftData(processedDraftData);
          console.log('Draft data loaded:', processedDraftData.length, 'records');
        } else {
          console.warn('Draft data sheet not found');
          setDraftData([]); // Set empty array if no draft data found
        }

        // ADD LEGACY DATA PROCESSING (LegacyByYear sheet - sheet 32)
        if (workbook.SheetNames.length >= 32) {
          const legacySheetName = workbook.SheetNames[31]; // 0-indexed, so sheet 32 is index 31
          const legacySheet = workbook.Sheets[legacySheetName];
          
          if (legacySheet) {
            const legacyJsonData = window.XLSX.utils.sheet_to_json(legacySheet);
            
            const processedLegacyData = legacyJsonData.map((row) => {
              const year = row.Year || row.year || row.YEAR;
              const manager = row.Manager || row.manager || row.MANAGER || row.Owner || row.owner;
              const legacyScore = parseFloat(row.LegacyScore || row.legacyscore || row.LEGACYSCORE || row.Score || 0);
              
              // Validate required fields
              if (!year || !manager || isNaN(legacyScore)) {
                return null;
              }
              
              return {
                Year: parseInt(year),
                Manager: String(manager).trim(),
                LegacyScore: legacyScore
              };
            }).filter(Boolean);
            
            setLegacyData(processedLegacyData);
            console.log('Legacy data loaded:', processedLegacyData.length, 'records');
          } else {
            console.warn('Legacy data sheet not found at index 31');
            setLegacyData([]);
          }
        } else {
          console.warn('Not enough sheets for legacy data (sheet 32)');
          setLegacyData([]);
        }

        // Process CurrentYearPR data
        if (workbook.SheetNames.includes('CurrenYearPR')) {
          const currentYearPRWorksheet = workbook.Sheets['CurrenYearPR'];
          const currentYearPRJsonData = window.XLSX.utils.sheet_to_json(currentYearPRWorksheet);
          
          // Clean and validate CurrentYearPR data
          const cleanedCurrentYearPR = currentYearPRJsonData.map(row => ({
            ...row,
            Year: parseInt(row.Year) || 0,
            Week: parseInt(row.Week) || 0,
            WeekID: parseInt(row.WeekID) || 0,
            Rank: parseInt(row.Rank) || 0,
            cumWins: parseInt(row.cumWins) || 0,
            cumLosses: parseInt(row.cumLosses) || 0,
            cumWinPct: parseFloat(row.cumWinPct) || 0,
            PF: parseFloat(row.PF) || 0
          }));
          
          setCurrentYearPR(cleanedCurrentYearPR);
          console.log('CurrentYearPR Data loaded:', cleanedCurrentYearPR.length, 'records');
        } else {
          console.warn('CurrenYearPR sheet not found');
        }

        // Process AllTimeRank data
        if (workbook.SheetNames.includes('AllTimeRank')) {
          const allTimeRankWorksheet = workbook.Sheets['AllTimeRank'];
          const allTimeRankJsonData = window.XLSX.utils.sheet_to_json(allTimeRankWorksheet);
          
          // Clean and validate AllTimeRank data
          const cleanedAllTimeRank = allTimeRankJsonData.map(row => ({
            ...row,
            Year: parseInt(row.Year) || 0,
            Week: parseInt(row.Week) || 0,
            WeekID: parseInt(row.WeekID) || 0,
            Rank: parseInt(row.Rank) || 0,
            cumWins: parseInt(row.cumWins) || 0,
            cumLosses: parseInt(row.cumLosses) || 0,
            cumWinPct: parseFloat(row.cumWinPct) || 0,
            cumGP: parseInt(row.cumGP) || 0
          }));
          
          setAllTimeRank(cleanedAllTimeRank);
          console.log('AllTimeRank Data loaded:', cleanedAllTimeRank.length, 'records');
        } else {
          console.warn('AllTimeRank sheet not found');
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error parsing Excel data:', err);
        setError('Error parsing Excel data: ' + err.message);
        setLoading(false);
      }
    };
    
    loadExcelData();
  }, []);

  return (
    <>
      {/* Mobile Top Navigation - Only visible on mobile */}
      <div className="lg:hidden">
        <div className={`fixed top-0 left-0 right-0 z-50 bg-gradient-to-r ${theme.mobileGradient} shadow-2xl border-b ${theme.mobileBorder}`}>
          <div className="flex justify-between items-center p-4 h-16">
            <div className="flex items-center">
              <div className="relative">
                <div className="absolute inset-0 bg-orange-500/50 rounded-full blur-lg animate-pulse"></div>
                <img 
                  src="/beasthead.png" 
                  alt="Beasts of Bellevue" 
                  className="relative h-10 w-10 mr-3 transition-all duration-300 object-contain drop-shadow-lg"
                />
              </div>
              <h1 className={`text-base font-bold ${theme.textPrimary}`}>Beasts of Bellevue</h1>
            </div>
            <button
              onClick={() => {
                console.log('Mobile menu toggle clicked', !isMobileMenuOpen);
                setIsMobileMenuOpen(!isMobileMenuOpen);
              }}
              className={`${theme.textSecondary} hover:${theme.textPrimary} p-2 rounded-lg transition-all duration-300 transform hover:scale-105 active:scale-95`}
            >
              <div className="relative w-6 h-6">
                <Menu className={`absolute inset-0 h-6 w-6 transition-all duration-300 transform ${
                  isMobileMenuOpen ? 'rotate-90 opacity-0 scale-75' : 'rotate-0 opacity-100 scale-100'
                }`} />
                <X className={`absolute inset-0 h-6 w-6 transition-all duration-300 transform ${
                  isMobileMenuOpen ? 'rotate-0 opacity-100 scale-100' : '-rotate-90 opacity-0 scale-75'
                }`} />
              </div>
            </button>
          </div>

          {/* Mobile Navigation Menu */}
          <div className={`overflow-hidden transition-all duration-500 ease-in-out ${
            isMobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
          }`}>
            <div className={`bg-gradient-to-r ${theme.mobileGradient} bg-opacity-95 backdrop-blur-sm border-t ${theme.mobileBorder} transform transition-transform duration-500 ease-in-out ${
              isMobileMenuOpen ? 'translate-y-0' : '-translate-y-full'
            }`}>
              <div className="px-2 pt-2 pb-3 space-y-1">
                {/* Regular navigation items */}
                {tabs.slice(0, -3).map((tab, index) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => {
                        handleTabChange(tab.id);
                        setIsMobileMenuOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 flex items-center space-x-3 transform ${
                        isMobileMenuOpen ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0'
                      } ${
                        activeTab === tab.id
                          ? `${theme.buttonActive} ${theme.textPrimary}`
                          : `${theme.textSecondary} hover:${theme.textPrimary}`
                      }`}
                      style={{
                        transitionDelay: isMobileMenuOpen ? `${index * 50}ms` : '0ms'
                      }}
                    >
                      <Icon className={`h-4 w-4 transition-all duration-300 transform ${
                        activeTab === tab.id ? theme.iconColor : `${theme.iconColor} group-hover:${theme.hoverIconColor} group-hover:scale-125 group-hover:rotate-12`
                      }`} />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
                
                {/* Mobile About Section - Discrete placement */}
                <div className="my-2 mx-3">
                  <div className={`h-px bg-gradient-to-r ${theme.borderColor} opacity-30`}></div>
                </div>
                
                <button
                  onClick={() => {
                    handleTabChange('about');
                    setIsMobileMenuOpen(false);
                  }}
                  className={`w-full text-left px-3 py-2 rounded-lg text-xs font-normal transition-all duration-300 flex items-center justify-center space-x-2 transform ${
                    isMobileMenuOpen ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0'
                  } ${
                    activeTab === 'about'
                      ? `${theme.buttonActive} ${theme.textPrimary}`
                      : `${theme.textSecondary} hover:${theme.textPrimary} opacity-70`
                  }`}
                  style={{
                    transitionDelay: isMobileMenuOpen ? `${(tabs.slice(0, -3).length) * 50}ms` : '0ms'
                  }}
                >
                  <BarChart3 className={`h-3 w-3 transition-all duration-300 ${
                    activeTab === 'about' ? theme.iconColor : theme.iconColor
                  }`} />
                  <span>About the League</span>
                </button>
                
                {/* Mobile separator for premium sections */}
                <div className="my-3 mx-3">
                  <div className={`h-px bg-gradient-to-r ${theme.borderColor} opacity-50`}></div>
                  <div className="flex justify-center mt-2 mb-2">
                    <div className="flex space-x-1">
                      <div className="w-1 h-1 bg-orange-400 rounded-full animate-pulse"></div>
                      <div className="w-1 h-1 bg-yellow-400 rounded-full animate-pulse delay-200"></div>
                      <div className="w-1 h-1 bg-red-400 rounded-full animate-pulse delay-400"></div>
                    </div>
                  </div>
                </div>
                
                {/* Premium Sections for Mobile (Celebrity Appearances + Hall Sections) */}
                {tabs.slice(-3).map((tab, index) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  const isChampions = tab.id === 'hall-of-champions';
                  const isSackos = tab.id === 'hall-of-sackos';
                  const isCelebrity = tab.id === 'celebrity-appearances';
                  const realIndex = tabs.slice(0, -3).length + index + 1; // Adjust index for delay calculation including about
                  
                  return (
                    <div key={tab.id} className="relative mx-1">
                      {/* Mobile Glassmorphism Background */}
                      <div className={`absolute inset-0 rounded-xl transition-all duration-500 backdrop-blur-xl ${
                        isActive 
                          ? isChampions 
                            ? 'bg-gradient-to-br from-yellow-400/40 via-amber-500/35 to-yellow-600/40 shadow-xl shadow-yellow-500/30' 
                            : isSackos
                            ? 'bg-gradient-to-br from-red-500/40 via-red-600/35 to-gray-800/40 shadow-xl shadow-red-500/30'
                            : 'bg-gradient-to-br from-orange-500/40 via-red-500/35 to-purple-600/40 shadow-xl shadow-orange-500/30'
                          : isChampions
                            ? 'bg-gradient-to-br from-yellow-500/15 via-amber-600/10 to-yellow-700/15'
                            : isSackos
                            ? 'bg-gradient-to-br from-red-600/15 via-red-700/10 to-gray-900/15'
                            : 'bg-gradient-to-br from-orange-500/15 via-red-500/10 to-purple-600/15'
                      }`}></div>
                      
                      <button
                        onClick={() => {
                          handleTabChange(tab.id);
                          setIsMobileMenuOpen(false);
                        }}
                        className={`relative w-full text-left px-4 py-3 rounded-xl text-sm font-bold transition-all duration-300 flex items-center space-x-3 transform ${
                          isMobileMenuOpen ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0'
                        } ${
                          isActive
                            ? 'text-white shadow-xl scale-105'
                            : isChampions
                              ? 'text-yellow-100 hover:text-white'
                              : isSackos
                              ? 'text-red-100 hover:text-white'
                              : 'text-orange-100 hover:text-white'
                        }`}
                        style={{
                          transitionDelay: isMobileMenuOpen ? `${realIndex * 50}ms` : '0ms'
                        }}
                      >
                        <div className="relative">
                          <Icon className={`h-5 w-5 transition-all duration-300 transform ${
                            isActive 
                              ? isChampions
                                ? 'text-yellow-200 drop-shadow-lg'
                                : isSackos
                                ? 'text-red-200 drop-shadow-lg'
                                : 'text-orange-200 drop-shadow-lg'
                              : isChampions
                                ? 'text-yellow-300 group-hover:text-white group-hover:scale-125 group-hover:rotate-12'
                                : isSackos
                                ? 'text-red-300 group-hover:text-white group-hover:scale-125 group-hover:-rotate-12'
                                : 'text-orange-300 group-hover:text-white group-hover:scale-125 group-hover:rotate-12'
                          }`} />
                          
                          {/* Mobile sparkle effect */}
                          <div className={`absolute -top-1 -right-1 w-2 h-2 rounded-full transition-all duration-300 ${
                            isActive
                              ? isChampions 
                                ? 'bg-yellow-200 animate-ping' 
                                : isSackos
                                ? 'bg-red-200 animate-ping'
                                : 'bg-orange-200 animate-ping'
                              : 'opacity-0'
                          }`}></div>
                        </div>
                        
                        <span className="tracking-wide">{tab.label}</span>
                        
                        {/* Mobile premium badge */}
                        <div className={`ml-auto transition-all duration-300 ${
                          isActive ? 'opacity-100' : 'opacity-60'
                        }`}>
                          <div className={`w-2 h-2 rounded-full ${
                            isChampions ? 'bg-yellow-300' : isSackos ? 'bg-red-300' : 'bg-orange-300'
                          } animate-pulse`}></div>
                        </div>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        
        {/* Mobile Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-12 pt-20">
          <Routes>
            <Route path="/" element={
              <HeadToHead 
                h2hData={h2hData} 
                finalData={finalData} 
                loading={loading} 
                error={error} 
                managers={managers} 
              />
            } />
            <Route path="/headtohead" element={
              <HeadToHead 
                h2hData={h2hData} 
                finalData={finalData} 
                loading={loading} 
                error={error} 
                managers={managers} 
              />
            } />
            <Route path="/standings" element={
              <AllTimeStandingsComponent 
                h2hData={h2hData} 
                finalData={finalData} 
                loading={loading} 
                error={error} 
              />
            } />
            <Route path="/powerrankings" element={
              <PowerRankings 
                h2hData={h2hData} 
                finalData={finalData} 
                currentYearPR={currentYearPR}
                allTimeRank={allTimeRank}
                loading={loading} 
                error={error} 
              />
            } />
            <Route path="/draftexplorer" element={<DraftExplorer />} />
            <Route path="/keepers" element={<Keepers />} />
            <Route path="/playerhistory" element={<PlayerHistory />} />
            <Route path="/warroom" element={<DraftWarRoom />} />
            <Route path="/managercards" element={
              <ManagerCards
                h2hData={h2hData}
                finalData={finalData} 
                draftData={draftData}
                legacyData={legacyData}
                loading={loading} 
                error={error} 
              />
            } />
            <Route path="/legacy" element={
              <LegacyScore 
                h2hData={h2hData} 
                finalData={finalData} 
                legacyData={legacyData}
                loading={loading} 
                error={error} 
              />
            } />
            <Route path="/recordbook" element={
              <RecordBookComponent 
                h2hData={h2hData} 
                finalData={finalData} 
                loading={loading} 
                error={error} 
              />
            } />
            <Route path="/ringoffame" element={
              <PlayerRingOfFame 
                draftData={draftData}
                loading={loading} 
                error={error} 
              />
            } />
            <Route path="/leagueperformance" element={
              <LeaguePerformance 
                h2hData={h2hData} 
                finalData={finalData} 
                loading={loading} 
                error={error} 
              />
            } />
            <Route path="/celebrityappearances" element={
              <CelebrityAppearances 
                loading={loading} 
                error={error} 
              />
            } />
            <Route path="/hallofchampions" element={
              <HallOfChampions 
                h2hData={h2hData}
                finalData={finalData}
                draftData={draftData}
                legacyData={legacyData}
                loading={loading}
                error={error}
              />
            } />
            <Route path="/hallofsackos" element={
              <HallOfSackos 
                h2hData={h2hData}
                finalData={finalData}
                draftData={draftData}
                legacyData={legacyData}
                loading={loading}
                error={error}
              />
            } />
            <Route path="/about" element={<About h2hData={h2hData} />} />
          </Routes>
        </main>
      </div>

      {/* Desktop Layout with Sidebar - Only visible on desktop */}
      <div className="hidden lg:flex h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50 overflow-hidden">
        {/* Desktop Sidebar - Fixed positioned */}
        <div className={`fixed left-0 top-0 h-full flex flex-col bg-gradient-to-b ${theme.sidebarGradient} shadow-2xl transition-all duration-500 ease-in-out z-10 ${sidebarCollapsed ? 'w-16' : 'w-64'}`}>
          {/* Sidebar Header */}
          <div className={`p-4 border-b ${theme.borderColor} flex-shrink-0`}>
            <div className="flex items-center justify-between">
              <div className={`flex items-center transition-all duration-500 ease-in-out overflow-hidden ${
                sidebarCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'
              }`}>
                <div className="relative">
                  <div className="absolute inset-0 bg-orange-500/50 rounded-full blur-xl animate-pulse"></div>
                  <img 
                    src="/beasthead.png" 
                    alt="Beasts of Bellevue" 
                    className="relative h-12 w-12 mr-3 transition-all duration-300 object-contain drop-shadow-lg"
                  />
                </div>
                <h1 className={`text-base font-bold ${theme.textPrimary} whitespace-nowrap`}>Beasts of Bellevue</h1>
              </div>
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className={`${theme.textSecondary} hover:${theme.textPrimary} p-2 rounded-lg transition-all duration-300 transform hover:scale-105 active:scale-95 flex-shrink-0`}
              >
                <div className="relative w-5 h-5">
                  <ChevronDown className={`absolute inset-0 h-5 w-5 transition-all duration-500 ease-in-out transform ${
                    sidebarCollapsed ? 'rotate-90 opacity-100' : 'rotate-0 opacity-0'
                  }`} />
                  <X className={`absolute inset-0 h-5 w-5 transition-all duration-500 ease-in-out transform ${
                    sidebarCollapsed ? 'rotate-0 opacity-0' : 'rotate-0 opacity-100'
                  }`} />
                </div>
              </button>
            </div>
            <div className={`flex justify-center transition-all duration-500 ease-in-out overflow-hidden ${
              sidebarCollapsed ? 'mt-2 max-h-12 opacity-100' : 'mt-0 max-h-0 opacity-0'
            }`}>
              <div className="relative">
                <div className="absolute inset-0 bg-orange-500/50 rounded-full blur-xl animate-pulse"></div>
                <img 
                  src="/beasthead.png" 
                  alt="Beasts of Bellevue" 
                  className="relative h-10 w-10 transition-all duration-300 object-contain drop-shadow-lg"
                />
              </div>
            </div>
          </div>

          {/* Sidebar Navigation - Scrollable if needed */}
          <nav className="flex-1 p-4 overflow-y-auto">
            <div className="space-y-2">
              {/* Regular navigation items */}
              {tabs.slice(0, -3).map((tab, index) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`group w-full flex items-center rounded-lg text-sm font-medium transition-all duration-300 transform active:scale-95 ${
                      sidebarCollapsed ? 'justify-center px-2 py-3' : 'px-3 py-3'
                    } ${
                      isActive
                        ? `${theme.buttonActive} ${theme.textPrimary} shadow-lg scale-105`
                        : `${theme.textSecondary} hover:scale-110`
                    }`}
                    title={sidebarCollapsed ? tab.label : ''}
                  >
                    {/* Icon with cartoonish animation - different behavior when collapsed */}
                    <Icon className={`h-5 w-5 flex-shrink-0 transition-all duration-300 transform ${
                      isActive 
                        ? theme.iconColor 
                        : sidebarCollapsed
                          ? `${theme.iconColor} group-hover:${theme.hoverIconColor} group-hover:scale-125 group-hover:rotate-12`
                          : `${theme.iconColor} group-hover:${theme.hoverIconColor} group-hover:scale-125 group-hover:rotate-12`
                    }`} />
                    
                    {/* Text with cartoonish animation */}
                    <span className={`ml-3 transition-all duration-500 ease-in-out overflow-hidden whitespace-nowrap font-bold ${
                      sidebarCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'
                    } ${
                      isActive
                        ? theme.textPrimary
                        : `${theme.textSecondary} group-hover:${theme.hoverTextColor} group-hover:scale-110 group-hover:tracking-wide`
                    }`}>
                      {tab.label}
                    </span>
                    
                    {/* Subtle glow effect on hover - only when expanded */}
                    {!sidebarCollapsed && (
                      <div className={`absolute inset-0 rounded-lg opacity-0 group-hover:opacity-20 transition-all duration-300 ${
                        activeTab === 'hall-of-sackos' ? 'bg-red-400' :
                        activeTab === 'hall-of-champions' ? 'bg-yellow-400' :
                        activeTab === 'celebrity-appearances' ? 'bg-orange-400' :
                        'bg-cyan-400'
                      } ${isActive ? 'opacity-0' : ''}`} />
                    )}
                  </button>
                );
              })}
              
              {/* Separator for Premium Experiences */}
              <div className={`my-6 transition-all duration-500 ease-in-out ${
                sidebarCollapsed ? 'mx-2' : 'mx-3'
              }`}>
                <div className={`h-px bg-gradient-to-r ${theme.borderColor} opacity-50`}></div>
                <div className="flex justify-center mt-2 mb-2">
                  <div className={`flex space-x-1 transition-all duration-500 ${
                    sidebarCollapsed ? 'opacity-0' : 'opacity-100'
                  }`}>
                    <div className="w-1 h-1 bg-orange-400 rounded-full animate-pulse"></div>
                    <div className="w-1 h-1 bg-yellow-400 rounded-full animate-pulse delay-200"></div>
                    <div className="w-1 h-1 bg-red-400 rounded-full animate-pulse delay-400"></div>
                  </div>
                </div>
              </div>
              
              {/* Premium Sections (Celebrity Appearances + Hall Sections) */}
              {tabs.slice(-3).map((tab, index) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                const isChampions = tab.id === 'hall-of-champions';
                const isSackos = tab.id === 'hall-of-sackos';
                const isCelebrity = tab.id === 'celebrity-appearances';
                
                return (
                  <div key={tab.id} className="relative">
                    {/* Enhanced Glassmorphism Background */}
                    <div className={`absolute inset-0 rounded-xl transition-all duration-500 backdrop-blur-xl ${
                      isActive 
                        ? isChampions 
                          ? 'bg-gradient-to-br from-yellow-400/40 via-amber-500/35 to-yellow-600/40 shadow-2xl shadow-yellow-500/40' 
                          : isSackos
                          ? 'bg-gradient-to-br from-red-500/40 via-red-600/35 to-gray-800/40 shadow-2xl shadow-red-500/40'
                          : 'bg-gradient-to-br from-orange-500/40 via-red-500/35 to-purple-600/40 shadow-2xl shadow-orange-500/40'
                        : isChampions
                          ? 'bg-gradient-to-br from-yellow-500/15 via-amber-600/10 to-yellow-700/15 group-hover:from-yellow-400/30 group-hover:via-amber-500/25 group-hover:to-yellow-600/30 group-hover:shadow-lg group-hover:shadow-yellow-500/20'
                          : isSackos
                          ? 'bg-gradient-to-br from-red-600/15 via-red-700/10 to-gray-900/15 group-hover:from-red-500/30 group-hover:via-red-600/25 group-hover:to-gray-800/30 group-hover:shadow-lg group-hover:shadow-red-500/20'
                          : 'bg-gradient-to-br from-orange-500/15 via-red-500/10 to-purple-600/15 group-hover:from-orange-400/30 group-hover:via-red-500/25 group-hover:to-purple-600/30 group-hover:shadow-lg group-hover:shadow-orange-500/20'
                    }`}></div>
                    
                    <button
                      onClick={() => handleTabChange(tab.id)}
                      className={`group relative w-full flex items-center rounded-xl text-sm font-bold transition-all duration-300 transform active:scale-95 ${
                        sidebarCollapsed ? 'justify-center px-2 py-4' : 'px-4 py-4'
                      } ${
                        isActive
                          ? 'text-white shadow-2xl scale-105'
                          : isChampions
                            ? 'text-yellow-100 hover:text-white hover:scale-110'
                            : isSackos
                            ? 'text-red-100 hover:text-white hover:scale-110'
                            : 'text-orange-100 hover:text-white hover:scale-110'
                      }`}
                      title={sidebarCollapsed ? tab.label : ''}
                    >
                      {/* Premium Icon with enhanced effects */}
                      <div className="relative">
                        <Icon className={`h-6 w-6 flex-shrink-0 transition-all duration-300 transform ${
                          isActive 
                            ? isChampions
                              ? 'text-yellow-200 drop-shadow-lg'
                              : isSackos
                              ? 'text-red-200 drop-shadow-lg'
                              : 'text-orange-200 drop-shadow-lg'
                            : isChampions
                              ? 'text-yellow-300 group-hover:text-white group-hover:scale-125 group-hover:rotate-12 group-hover:drop-shadow-2xl'
                              : isSackos
                              ? 'text-red-300 group-hover:text-white group-hover:scale-125 group-hover:-rotate-12 group-hover:drop-shadow-2xl'
                              : 'text-orange-300 group-hover:text-white group-hover:scale-125 group-hover:rotate-12 group-hover:drop-shadow-2xl'
                        }`} />
                        
                        {/* Premium sparkle effect */}
                        {!sidebarCollapsed && (
                          <div className={`absolute -top-1 -right-1 w-2 h-2 rounded-full transition-all duration-300 ${
                            isActive
                              ? isChampions 
                                ? 'bg-yellow-200 animate-ping' 
                                : isSackos
                                ? 'bg-red-200 animate-ping'
                                : 'bg-orange-200 animate-ping'
                              : 'opacity-0 group-hover:opacity-100'
                          } ${
                            isChampions ? 'group-hover:bg-yellow-100' : isSackos ? 'group-hover:bg-red-100' : 'group-hover:bg-orange-100'
                          } group-hover:animate-pulse`}></div>
                        )}
                      </div>
                      
                      {/* Premium Text with proper sizing */}
                      <span className={`ml-3 transition-all duration-500 ease-in-out overflow-hidden whitespace-nowrap font-bold tracking-wide ${
                        sidebarCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'
                      } ${
                        isActive
                          ? 'text-white drop-shadow-lg'
                          : isChampions
                            ? 'text-yellow-100 group-hover:text-white group-hover:scale-105 group-hover:tracking-wider group-hover:drop-shadow-xl'
                            : isSackos
                            ? 'text-red-100 group-hover:text-white group-hover:scale-105 group-hover:tracking-wider group-hover:drop-shadow-xl'
                            : 'text-orange-100 group-hover:text-white group-hover:scale-105 group-hover:tracking-wider group-hover:drop-shadow-xl'
                      }`}>
                        {tab.label}
                      </span>
                      
                      {/* Premium Badge */}
                      {!sidebarCollapsed && (
                        <div className={`ml-auto transition-all duration-300 ${
                          isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                        }`}>
                          <div className={`w-2 h-2 rounded-full ${
                            isChampions ? 'bg-yellow-300' : isSackos ? 'bg-red-300' : 'bg-orange-300'
                          } animate-pulse`}></div>
                        </div>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          </nav>

          {/* Sidebar Footer */}
          <div className={`p-4 border-t ${theme.borderColor} flex-shrink-0`}>
            <div className={`transition-all duration-500 ease-in-out overflow-hidden ${
              sidebarCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'
            }`}>
              <button
                onClick={() => handleTabChange('about')}
                className={`w-full text-xs ${theme.textSecondary} hover:${theme.textPrimary} text-center whitespace-nowrap transition-all duration-300 py-2 px-3 rounded-lg hover:bg-white/5 ${
                  activeTab === 'about' ? `${theme.buttonActive} ${theme.textPrimary}` : ''
                }`}
              >
                About the Beasts of Bellevue
              </button>
            </div>
            <div className={`flex justify-center transition-all duration-500 ease-in-out overflow-hidden ${
              sidebarCollapsed ? 'max-h-4 opacity-100' : 'max-h-0 opacity-0'
            }`}>
              <button
                onClick={() => handleTabChange('about')}
                className={`p-2 rounded-lg transition-all duration-300 hover:bg-white/5 ${
                  activeTab === 'about' ? `${theme.buttonActive}` : ''
                }`}
                title="About the Beasts of Bellevue"
              >
                <BarChart3 className={`h-4 w-4 ${theme.textSecondary} hover:${theme.textPrimary} transition-all duration-300 ${
                  activeTab === 'about' ? theme.iconColor : ''
                }`} />
              </button>
            </div>
          </div>
        </div>
        
        {/* Desktop Content - With left margin to account for sidebar */}
        <main className={`flex-1 h-full overflow-y-auto transition-all duration-300 ${sidebarCollapsed ? 'ml-16' : 'ml-64'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-12">
            <Routes>
              <Route path="/" element={
                <HeadToHead 
                  h2hData={h2hData} 
                  finalData={finalData} 
                  loading={loading} 
                  error={error} 
                  managers={managers} 
                />
              } />
              <Route path="/headtohead" element={
                <HeadToHead 
                  h2hData={h2hData} 
                  finalData={finalData} 
                  loading={loading} 
                  error={error} 
                  managers={managers} 
                />
              } />
              <Route path="/standings" element={
                <AllTimeStandingsComponent 
                  h2hData={h2hData} 
                  finalData={finalData} 
                  loading={loading} 
                  error={error} 
                />
              } />
              <Route path="/powerrankings" element={
                <PowerRankings 
                  h2hData={h2hData} 
                  finalData={finalData} 
                  currentYearPR={currentYearPR}
                  allTimeRank={allTimeRank}
                  loading={loading} 
                  error={error} 
                />
              } />
              <Route path="/draftexplorer" element={<DraftExplorer />} />
            <Route path="/keepers" element={<Keepers />} />
            <Route path="/playerhistory" element={<PlayerHistory />} />
            <Route path="/warroom" element={<DraftWarRoom />} />
              <Route path="/managercards" element={
                <ManagerCards
                  h2hData={h2hData}
                  finalData={finalData} 
                  draftData={draftData}
                  legacyData={legacyData}
                  loading={loading} 
                  error={error} 
                />
              } />
              <Route path="/legacy" element={
                <LegacyScore 
                  h2hData={h2hData} 
                  finalData={finalData} 
                  legacyData={legacyData}
                  loading={loading} 
                  error={error} 
                />
              } />
              <Route path="/recordbook" element={
                <RecordBookComponent 
                  h2hData={h2hData} 
                  finalData={finalData} 
                  loading={loading} 
                  error={error} 
                />
              } />
              <Route path="/ringoffame" element={
                <PlayerRingOfFame 
                  draftData={draftData}
                  loading={loading} 
                  error={error} 
                />
              } />
              <Route path="/leagueperformance" element={
                <LeaguePerformance 
                  h2hData={h2hData} 
                  finalData={finalData} 
                  loading={loading} 
                  error={error} 
                />
              } />
              <Route path="/celebrityappearances" element={
                <CelebrityAppearances 
                  loading={loading} 
                  error={error} 
                />
              } />
              <Route path="/hallofchampions" element={
                <HallOfChampions 
                  h2hData={h2hData}
                  finalData={finalData}
                  draftData={draftData}
                  legacyData={legacyData}
                  loading={loading}
                  error={error}
                />
              } />
              <Route path="/hallofsackos" element={
                <HallOfSackos 
                  h2hData={h2hData}
                  finalData={finalData}
                  draftData={draftData}
                  legacyData={legacyData}
                  loading={loading}
                  error={error}
                />
              } />
              <Route path="/about" element={<About h2hData={h2hData} />} />
            </Routes>
          </div>
        </main>
      </div>
    </>
  );
};

// Main App Component with Router Wrapper
const App = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default App;